import { Component, Input } from '@angular/core';
import { UrgencyBoostExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { ViewMode } from '../../../../models';
import { polarisPaddingToSpacing } from '../../config/polaris-padding';

interface SampleLineItem {
  productId: string;
  name: string;
  price: string;
  quantity: number;
}

const SAMPLE_LINE_ITEMS: SampleLineItem[] = [
  { productId: '1', name: 'Monstera Deliciosa', price: '$100.00', quantity: 2 },
  { productId: '2', name: 'Dracaena Trifasciata', price: '$45.00', quantity: 1 },
];

const SAMPLE_PLACEHOLDER_VALUES: Record<string, string | number> = {
  '{count}': 12,
  '{hours}': 24,
  '{days}': 3,
};

interface RenderedBadge {
  id: string;
  text: string;
  icon?: string | null;
  backgroundColor?: string;
  textColor?: string;
  cornerRadius?: string;
}

@Component({
  selector: 'urgency-boost-example',
  templateUrl: './urgency-boost-example.component.html',
  styleUrls: ['./urgency-boost-example.component.scss'],
  standalone: false,
})
export class UrgencyBoostExampleComponent {
  @Input() template: UrgencyBoostExtension.Template;
  @Input() mode: ViewMode = null;
  @Input() isMobile = false;

  sampleLineItems = SAMPLE_LINE_ITEMS;

  cornerRadiusToPx(value: string | undefined): string {
    switch (value) {
      case 'none':
        return '0px';
      case 'small':
        return '2px';
      case 'base':
        return '5px';
      case 'large':
        return '10px';
      case 'fully rounded':
        return '36px';
      default:
        return '10px';
    }
  }
  /** Preview px for the Polaris padding token above the badges row (shared scale). */
  spaceAboveBadgeToPadding(value: string | undefined): string {
    return polarisPaddingToSpacing(value);
  }

  /** Render the union of all configured badges (preview ignores per-group rule matching). */
  renderBadgesFor(_lineItem: SampleLineItem): RenderedBadge[] {
    const groups = this.template?.boostGroups ?? [];
    const cap = this.template?.maxBadgesPerProduct ?? null;
    const out: RenderedBadge[] = [];

    for (const group of groups) {
      for (const badge of group.badges ?? []) {
        const text = this.renderTemplateText(badge.richTextTemplate ?? '');
        if (!text) continue;
        out.push({
          id: badge.id,
          text,
          icon: badge.icon,
          backgroundColor: badge.backgroundColor,
          textColor: badge.textColor,
          cornerRadius: badge.cornerRadius,
        });
      }
    }

    if (cap !== null && cap !== undefined && cap > 0) {
      return out.slice(0, cap);
    }
    return out;
  }

  private renderTemplateText(template: string): string {
    if (!template) return '';
    const plainText = template.replace(/<[^>]*>/g, '').trim();
    if (!plainText) return '';
    let out = template
      .replace(/<\/?(?:p|div|br|ul|ol|li|h[1-6]|blockquote)[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    for (const [token, value] of Object.entries(SAMPLE_PLACEHOLDER_VALUES)) {
      out = out.split(token).join(String(value));
    }
    return out;
  }
}
